import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, Users, TrendingUp, TrendingDown, AlertTriangle,
  Star, Activity, Shield, DollarSign, ChevronDown, ChevronRight,
  UserCheck, UserX
} from "lucide-react";
import { Card } from "../shared/UIComponents";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_CONFIG = {
  high: { label: "High", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", bar: "bg-emerald-500" },
  medium: { label: "Medium", bg: "bg-amber-50", color: "text-amber-700", border: "border-amber-200", bar: "bg-amber-500" },
  low: { label: "Low", bg: "bg-red-50", color: "text-red-700", border: "border-red-200", bar: "bg-red-400" },
};

function ScoreBar({ score }) {
  const color = score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-400";
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${score}%` }}
          transition={{ duration: 0.6 }} className={`h-full rounded-full ${color}`} />
      </div>
      <span className="text-xs font-bold text-[#222] w-8 text-right">{score}</span>
    </div>
  );
}

function UserRow({ user, rank }) {
  const [expanded, setExpanded] = useState(false);
  const band = BAND_CONFIG[user.band] || BAND_CONFIG.medium;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all mb-2 ${expanded ? "border-[#D4AF37]/40 shadow-sm" : "border-[#EDE8DC]"}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#faf8f3]/60" onClick={() => setExpanded(!expanded)} data-testid={`engagement-user-${user.user_id}`}>
        <div className="w-6 text-center text-[10px] font-bold text-[#999]">#{rank}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#222] truncate">{user.full_name || "Unknown"}</div>
          <div className="text-[10px] text-[#888] truncate">{user.email}</div>
        </div>
        <div className="w-32"><ScoreBar score={user.score} /></div>
        <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${band.bg} ${band.color} ${band.border} border`}>{band.label}</span>
        {expanded ? <ChevronDown className="h-3 w-3 text-[#aaa]" /> : <ChevronRight className="h-3 w-3 text-[#aaa]" />}
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-[#EDE8DC] bg-[#faf8f3]/40">
          <div className="flex gap-3 mb-2 flex-wrap">
            {Object.entries(user.metrics || {}).map(([key, val]) => (
              <div key={key} className="text-[10px]">
                <span className="text-[#888]">{key.replace(/_/g, " ")}:</span>{" "}
                <span className="font-bold text-[#222]">{typeof val === "number" && val > 1000 ? `$${(val / 1000).toFixed(0)}K` : val}</span>
              </div>
            ))}
          </div>
          {user.signals?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {user.signals.map((s, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full text-[8px] font-medium bg-white border border-[#EDE8DC] text-[#666]">{s}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EngagementScoringPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const headers = { Authorization: `Bearer ${localStorage.getItem("ic_token")}` };

  useEffect(() => {
    axios.get(`${API}/admin/engagement-scores`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-[#888]">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading engagement scores...
    </div>
  );

  if (!data) return null;

  const { summary, borrowers = [], lenders = [], investors = [] } = data;
  const allUsers = [...borrowers, ...lenders, ...investors].sort((a, b) => b.score - a.score);

  const tabs = [
    { key: "all", label: "All Users", count: allUsers.length },
    { key: "borrowers", label: "Borrowers", count: borrowers.length },
    { key: "lenders", label: "Lenders", count: lenders.length },
    { key: "investors", label: "Investors", count: investors.length },
    { key: "at_risk", label: "At Risk", count: allUsers.filter(u => u.band === "low").length },
  ];

  const displayUsers = activeTab === "all" ? allUsers
    : activeTab === "at_risk" ? allUsers.filter(u => u.band === "low")
    : data[activeTab] || [];

  return (
    <div data-testid="engagement-scoring-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[#222] flex items-center gap-2">
            <Activity className="h-5 w-5 text-[#D4AF37]" /> Engagement Scoring
          </h2>
          <p className="text-xs text-[#888] mt-0.5">Platform-wide user engagement analysis across all roles</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 sm:grid-cols-5 mb-6">
        {[
          { label: "Total Users", value: summary.total_users, icon: Users, color: "text-blue-600" },
          { label: "Avg Score", value: summary.avg_score, icon: TrendingUp, color: "text-emerald-600" },
          { label: "High Engagement", value: summary.high_engagement, icon: UserCheck, color: "text-emerald-600" },
          { label: "Medium", value: summary.medium_engagement, icon: Star, color: "text-amber-600" },
          { label: "At Risk", value: summary.at_risk, icon: AlertTriangle, color: "text-red-500" },
        ].map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
            <Card hover={false}>
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-cream p-2 border border-cream-warm"><s.icon className={`h-4 w-4 ${s.color}`} /></div>
                <div>
                  <div className="text-xl font-bold text-[#0A0A0A]">{s.value}</div>
                  <div className="text-[10px] text-[#888] uppercase tracking-wider">{s.label}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Engagement Distribution Bar */}
      <Card hover={false} className="mb-6">
        <div className="text-xs font-bold text-[#555] mb-2 uppercase tracking-wider">Engagement Distribution</div>
        <div className="flex h-6 rounded-lg overflow-hidden border border-[#EDE8DC]">
          {summary.high_engagement > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${(summary.high_engagement / summary.total_users) * 100}%` }} />}
          {summary.medium_engagement > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${(summary.medium_engagement / summary.total_users) * 100}%` }} />}
          {summary.at_risk > 0 && <div className="bg-red-400 transition-all" style={{ width: `${(summary.at_risk / summary.total_users) * 100}%` }} />}
        </div>
        <div className="flex gap-4 mt-2 text-[10px] text-[#666]">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" /> High ({summary.high_engagement})</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-400" /> Medium ({summary.medium_engagement})</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-400" /> At Risk ({summary.at_risk})</span>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${activeTab === t.key ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-white border-[#EDE8DC] text-[#666] hover:border-emerald-200"}`}
            data-testid={`engagement-tab-${t.key}`}>
            {t.label} <span className="ml-1 text-[10px] opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {/* User List */}
      <div>
        {displayUsers.length === 0 ? (
          <Card hover={false}>
            <div className="text-center py-8 text-sm text-[#888]">No users in this category</div>
          </Card>
        ) : (
          displayUsers.map((user, i) => <UserRow key={user.user_id} user={user} rank={i + 1} />)
        )}
      </div>
    </div>
  );
}
